import { create } from 'zustand';
import { authAPI } from '../services/api';

interface UserInfo {
  id: number;
  username: string;
  email: string | null;
  is_admin: boolean;
  monthly_cost_limit: number | null;
  is_active: boolean;
  must_reset_password: boolean;
}

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setupRequired: boolean;
  mustResetPassword: boolean;
  user: UserInfo | null;
  login: (username: string, password: string) => Promise<{ mustResetPassword: boolean }>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  fetchUser: () => Promise<void>;
  setInitialPassword: (newPassword: string) => Promise<void>;
  clearMustResetPassword: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: true,
  setupRequired: false,
  mustResetPassword: false,
  user: null,

  login: async (username: string, password: string) => {
    const response = await authAPI.login(username, password);
    const { access_token, must_reset_password } = response.data;
    localStorage.setItem('token', access_token);
    set({
      token: access_token,
      isAuthenticated: true,
      mustResetPassword: must_reset_password
    });
    // Fetch user info after login
    await get().fetchUser();
    return { mustResetPassword: must_reset_password };
  },

  logout: () => {
    localStorage.removeItem('token');
    set({ token: null, isAuthenticated: false, user: null, mustResetPassword: false });
  },

  fetchUser: async () => {
    try {
      const response = await authAPI.me();
      const userData = response.data;
      set({
        user: userData,
        mustResetPassword: userData.must_reset_password
      });
    } catch (error) {
      set({ user: null });
    }
  },

  setInitialPassword: async (newPassword: string) => {
    await authAPI.setInitialPassword(newPassword);
    set({ mustResetPassword: false });
    // Refresh user info
    await get().fetchUser();
  },

  clearMustResetPassword: () => {
    set({ mustResetPassword: false });
  },

  checkAuth: async () => {
    try {
      const response = await authAPI.status();
      const { setup_required, ready } = response.data;

      if (setup_required) {
        set({ isLoading: false, setupRequired: true, isAuthenticated: false, user: null, mustResetPassword: false });
        return;
      }

      const token = localStorage.getItem('token');
      const isAuthenticated = !!token && ready;

      set({
        isLoading: false,
        setupRequired: false,
        isAuthenticated,
      });

      // Fetch user info if authenticated
      if (isAuthenticated) {
        await get().fetchUser();
      }
    } catch (error) {
      set({ isLoading: false, isAuthenticated: false, user: null, mustResetPassword: false });
    }
  },
}));
